import { useCallback, useEffect, useMemo, useState } from 'react'

import { PROVIDERS } from 'consts'
import type { TEntityUpdateStatus } from 'entities/post'
import { usePostMutations, usePostQueries } from 'hooks-queries/post'
import { FeatureFlagsStore } from 'stores'
import { analyticsTracker } from 'utils'

import { POLL_INTERVAL_MS, RESPONSE_POST_LOG, UPDATE_POST_LOG } from './video-info.consts'
import type { IVideoInfoProps, IVideoInfoReturnProps } from './video-info.types'
import { ENTITY_UPDATE_STATUS } from '../common/posts.consts'

export function useVideoInfoModel({ video }: IVideoInfoProps): IVideoInfoReturnProps {
  const { provider, creator, title, creatorId, live, publishedAt, minimalVersion, postId, postUrl } = video

  const [errorStatus, setErrorStatus] = useState<TEntityUpdateStatus | null>(null)

  const isUpdatePostEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isUpdatePostEnabled')

  const [updatePost, { loading: isUpdatingPost }] = usePostMutations({
    mutationName: 'updatePost',
    errorLog: UPDATE_POST_LOG,
  })

  const formattedPostId = useMemo(() => {
    if (!provider || !postId) {
      return ''
    }
    return `${provider}:${postId}`
  }, [provider, postId])

  const verifyUpdatePost = (provider === 'tt' || provider === 'in') && isUpdatePostEnabled && !!formattedPostId

  const handleUpdatePost = useCallback(() => {
    setErrorStatus(null)
    const { POST } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(POST.CLICK_UPDATE, {
      'Page title': 'Video Modal',
      Tab: 'Videos',
      'Post id': formattedPostId,
      Title: title,
    })
    updatePost({
      variables: {
        postId: formattedPostId,
      },
      refetchQueries: ['getPostUpdateStatus'],
      awaitRefetchQueries: true,
    }).catch(() => {
      setErrorStatus(ENTITY_UPDATE_STATUS.FAILED)
    })
  }, [formattedPostId, title, updatePost])

  const providerName = PROVIDERS?.[provider]?.NAME

  const {
    data,
    error: queryError,
    loading,
    startPolling,
    stopPolling,
  } = usePostQueries({
    errorLog: RESPONSE_POST_LOG,
    queryName: 'getPostUpdateStatus',
    queryVariables: { postId: formattedPostId },
    options: {
      skip: !verifyUpdatePost,
    },
  })

  useEffect(() => {
    const shouldPoll = isUpdatingPost || data?.canUpdate?.status === ENTITY_UPDATE_STATUS.RUNNING

    if (shouldPoll) {
      startPolling(POLL_INTERVAL_MS)
    } else {
      stopPolling()
    }

    return () => {
      stopPolling()
    }
  }, [isUpdatingPost, data?.canUpdate?.status, startPolling, stopPolling])

  const hasQueryError = !!queryError && !data && !loading

  const statusPostResponse = useMemo(() => {
    return data?.canUpdate ?? null
  }, [data])

  useEffect(() => {
    setErrorStatus(null)
  }, [formattedPostId])

  const statusPost = useMemo((): TEntityUpdateStatus | null => {
    return errorStatus ?? data?.canUpdate?.status ?? null
  }, [errorStatus, data?.canUpdate?.status])

  return {
    providerName,
    creator,
    title,
    creatorId,
    provider,
    publishedAt,
    live,
    minimalVersion,
    handleUpdatePost,
    isUpdatingPost,
    verifyUpdatePost,
    statusPost,
    formattedPostId,
    statusPostResponse,
    hasQueryError,
    postUrl,
  }
}
